import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  Download, Send, Users, Trophy, Euro, Repeat, Search, Mail, MessageCircle, RefreshCw, Crown,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import {
  buildClientStats, groupByBucket, REACTIVATION_BUCKETS, ClientStat,
} from "@/lib/client-analytics";
import { exportSheets, todayStamp } from "@/lib/report-export";
import { useNavigate } from "react-router-dom";

const eur = (n: number) => `€${(n || 0).toFixed(2)}`;

const leadRow = (c: ClientStat, faixa?: string) => ({
  Nome: c.name || "-",
  Email: c.email || "",
  Telemóvel: c.phone || "",
  "Última reserva": c.lastDate ? format(parseISO(c.lastDate), "dd/MM/yyyy") : "-",
  "Dias sem reservar": c.daysSinceLast ?? "-",
  Reservas: c.bookings,
  Encomendas: c.orders,
  "Total gasto (€)": Number(c.totalRevenue.toFixed(2)),
  "Ticket médio (€)": Number(c.avgTicket.toFixed(2)),
  ...(faixa ? { Faixa: faixa } : {}),
});

const DEFAULT_SUBJECT = "Sentimos a sua falta na Mente Sã 💛";
const DEFAULT_EMAIL = `Olá {{primeiro_nome}},

Notámos que já passou algum tempo desde a sua última visita à Mente Sã e queríamos saber como está.

Preparámos um momento só para si: reserve a sua próxima sessão e volte a cuidar do seu bem-estar.

Estamos à sua espera!

Com carinho,
Equipa Mente Sã`;
const DEFAULT_WHATS = `Olá {{primeiro_nome}}! 💛

Aqui é a equipa Mente Sã. Já passou algum tempo desde a sua última sessão e adoraríamos voltar a recebê-la.

Quer marcar a sua próxima sessão? É só responder a esta mensagem.

Com carinho,
Mente Sã`;

const Reports = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<ClientStat[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [activeBuckets, setActiveBuckets] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [rankSort, setRankSort] = useState<"visits" | "revenue" | "ticket" | "recent">("revenue");
  const [rankLimit, setRankLimit] = useState("25");

  // Campaign dialog
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [chEmail, setChEmail] = useState(true);
  const [chWhats, setChWhats] = useState(true);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL);
  const [whatsBody, setWhatsBody] = useState(DEFAULT_WHATS);

  const fetchData = async () => {
    setLoading(true);
    const [{ data: b }, { data: o }] = await Promise.all([
      supabase.from("bookings").select("client_name, client_email, client_phone, booking_date, total_price, status, locale, created_at").neq("status", "blocked").order("booking_date", { ascending: false }),
      supabase.from("orders").select("client_name, client_email, client_phone, created_at, total_amount, status, locale").order("created_at", { ascending: false }),
    ]);
    setBookings(b || []);
    setOrders(o || []);
    setClients(buildClientStats(b || [], o || []));
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const buckets = useMemo(() => groupByBucket(clients), [clients]);

  const bucketChart = useMemo(
    () => REACTIVATION_BUCKETS.map((b) => ({ name: b.label.replace(" dias", ""), total: buckets[b.id]?.length || 0, id: b.id })),
    [buckets],
  );

  const visibleBucketIds = useMemo(() => {
    const any = Object.values(activeBuckets).some(Boolean);
    return any ? REACTIVATION_BUCKETS.filter((b) => activeBuckets[b.id]).map((b) => b.id) : REACTIVATION_BUCKETS.map((b) => b.id);
  }, [activeBuckets]);

  const visibleLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list: { bucketId: string; label: string; client: ClientStat }[] = [];
    for (const b of REACTIVATION_BUCKETS) {
      if (!visibleBucketIds.includes(b.id)) continue;
      for (const c of buckets[b.id] || []) {
        if (q && !(`${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q))) continue;
        list.push({ bucketId: b.id, label: b.label, client: c });
      }
    }
    return list;
  }, [buckets, visibleBucketIds, search]);

  const selectedClients = useMemo(
    () => visibleLeads.filter((l) => selected[l.client.key]).map((l) => l.client),
    [visibleLeads, selected],
  );

  const toggleAllVisible = (on: boolean) => {
    const next = { ...selected };
    for (const l of visibleLeads) next[l.client.key] = on;
    setSelected(next);
  };

  // ---------- Exportações ----------
  const exportSelected = () => {
    const rows = selectedClients.map((c) => leadRow(c));
    if (!exportSheets([{ name: "Selecionados", rows }], `leads-selecionados-${todayStamp()}`)) {
      toast({ title: "Nada para exportar", variant: "destructive" });
    }
  };

  const exportAllTogether = () => {
    const rows: any[] = [];
    for (const b of REACTIVATION_BUCKETS) {
      for (const c of buckets[b.id] || []) rows.push(leadRow(c, b.label));
    }
    if (!exportSheets([{ name: "Todos os leads", rows }], `leads-reativacao-${todayStamp()}`)) {
      toast({ title: "Nada para exportar", variant: "destructive" });
    }
  };

  const exportSeparated = () => {
    const sheets = REACTIVATION_BUCKETS.map((b) => ({
      name: b.label,
      rows: (buckets[b.id] || []).map((c) => leadRow(c, b.label)),
    }));
    if (!exportSheets(sheets, `leads-por-faixa-${todayStamp()}`)) {
      toast({ title: "Nada para exportar", variant: "destructive" });
    }
  };

  const exportBucket = (id: string, label: string) => {
    const rows = (buckets[id] || []).map((c) => leadRow(c, label));
    if (!exportSheets([{ name: label, rows }], `leads-${id}-${todayStamp()}`)) {
      toast({ title: "Faixa sem clientes", variant: "destructive" });
    }
  };

  // ---------- Ranking ----------
  const ranking = useMemo(() => {
    const list = [...clients];
    list.sort((a, b) => {
      if (rankSort === "visits") return (b.bookings + b.orders) - (a.bookings + a.orders);
      if (rankSort === "ticket") return b.avgTicket - a.avgTicket;
      if (rankSort === "recent") return (a.daysSinceLast ?? 99999) - (b.daysSinceLast ?? 99999);
      return b.totalRevenue - a.totalRevenue;
    });
    const n = rankLimit === "all" ? list.length : parseInt(rankLimit, 10);
    return list.slice(0, n);
  }, [clients, rankSort, rankLimit]);

  const exportRanking = () => {
    const rows = ranking.map((c, i) => ({ "#": i + 1, ...leadRow(c) }));
    if (!exportSheets([{ name: "Ranking clientes", rows }], `ranking-clientes-${todayStamp()}`)) {
      toast({ title: "Nada para exportar", variant: "destructive" });
    }
  };

  // ---------- Resumo ----------
  const summary = useMemo(() => {
    const totalClients = clients.length;
    const recurring = clients.filter((c) => c.bookings + c.orders > 1).length;
    const revenue = clients.reduce((s, c) => s + c.totalRevenue, 0);
    const visits = clients.reduce((s, c) => s + c.bookings + c.orders, 0);
    const inactive = clients.filter((c) => (c.daysSinceLast ?? 0) >= 30).length;
    return {
      totalClients,
      recurring,
      recurringPct: totalClients ? (recurring / totalClients) * 100 : 0,
      avgTicket: visits ? revenue / visits : 0,
      ltv: totalClients ? revenue / totalClients : 0,
      inactive,
      revenue,
      visits,
    };
  }, [clients]);

  // ---------- Envio ----------
  const openCampaign = () => {
    if (!selectedClients.length) {
      toast({ title: "Selecione pelo menos um cliente", variant: "destructive" });
      return;
    }
    setCampaignOpen(true);
  };

  const sendCampaign = async () => {
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-campaign", {
        body: {
          channels: { email: chEmail, whatsapp: chWhats },
          subject,
          emailBody,
          whatsappBody: whatsBody,
          recipients: selectedClients.map((c) => ({
            name: c.name, email: c.email, phone: c.phone, locale: c.locale,
          })),
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      const d: any = data;
      toast({
        title: "Campanha enviada",
        description: `Emails: ${d.email_sent} enviados / ${d.email_failed} falhas · WhatsApp: ${d.whatsapp_sent} enviados / ${d.whatsapp_failed} falhas`,
      });
      setCampaignOpen(false);
    } catch (e: any) {
      toast({ title: "Falha no envio", description: e.message || "Erro desconhecido", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">A carregar relatórios...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Relatórios & CRM</h1>
          <p className="text-sm text-muted-foreground">Reativação de clientes, ranking e indicadores de fidelização.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw size={15} className="mr-2" /> Atualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { icon: Users, label: "Clientes únicos", value: String(summary.totalClients) },
          { icon: Repeat, label: "Clientes recorrentes", value: `${summary.recurring} (${summary.recurringPct.toFixed(0)}%)` },
          { icon: Euro, label: "Ticket médio geral", value: eur(summary.avgTicket) },
          { icon: Crown, label: "Valor médio por cliente", value: eur(summary.ltv) },
          { icon: Send, label: "Inativos +30 dias", value: String(summary.inactive) },
        ].map((k) => (
          <Card key={k.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <k.icon size={14} /> {k.label}
              </div>
              <p className="text-xl font-bold text-foreground">{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="reactivation">
        <TabsList>
          <TabsTrigger value="reactivation">Reativação</TabsTrigger>
          <TabsTrigger value="ranking">Ranking de clientes</TabsTrigger>
        </TabsList>

        {/* ---------------- REATIVAÇÃO ---------------- */}
        <TabsContent value="reactivation" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Clientes por tempo sem reservar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bucketChart}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <RTooltip formatter={(v: any) => [`${v} clientes`, "Total"]} />
                    <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                      {bucketChart.map((entry) => (
                        <Cell key={entry.id} fill="hsl(var(--primary))" opacity={activeBuckets[entry.id] ? 1 : 0.75} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {REACTIVATION_BUCKETS.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setActiveBuckets((p) => ({ ...p, [b.id]: !p[b.id] }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      activeBuckets[b.id]
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted/40 text-muted-foreground border-border hover:bg-muted"
                    }`}
                  >
                    {b.label} · {buckets[b.id]?.length || 0}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={exportAllTogether}>
                  <Download size={15} className="mr-2" /> Exportar tudo (1 folha)
                </Button>
                <Button size="sm" variant="outline" onClick={exportSeparated}>
                  <Download size={15} className="mr-2" /> Exportar separado por faixa
                </Button>
                {REACTIVATION_BUCKETS.filter((b) => (buckets[b.id]?.length || 0) > 0).map((b) => (
                  <Button key={b.id} size="sm" variant="ghost" className="text-xs" onClick={() => exportBucket(b.id, b.label)}>
                    <Download size={13} className="mr-1" /> {b.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-base">
                  Leads para follow-up
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {visibleLeads.length} clientes · {selectedClients.length} selecionados
                  </span>
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Procurar..." className="pl-8 h-9 w-44" />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => toggleAllVisible(true)}>Selecionar todos</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected({})}>Limpar</Button>
                  <Button size="sm" variant="outline" onClick={exportSelected} disabled={!selectedClients.length}>
                    <Download size={15} className="mr-2" /> Exportar seleção
                  </Button>
                  <Button size="sm" onClick={openCampaign} disabled={!selectedClients.length}>
                    <Send size={15} className="mr-2" /> Enviar follow-up
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[520px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Contactos</TableHead>
                      <TableHead>Faixa</TableHead>
                      <TableHead className="text-right">Dias</TableHead>
                      <TableHead className="text-right">Reservas</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Ticket médio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleLeads.map(({ client: c, label }) => (
                      <TableRow key={c.key} className="cursor-pointer" onClick={() => setSelected((p) => ({ ...p, [c.key]: !p[c.key] }))}>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={!!selected[c.key]}
                            onCheckedChange={(v) => setSelected((p) => ({ ...p, [c.key]: !!v }))}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              const params = new URLSearchParams();
                              params.set("search", c.name || "");
                              if (c.email) params.set("email", c.email);
                              if (c.phone) params.set("phone", c.phone);
                              navigate(`/admin/clients?${params.toString()}`);
                            }}
                            className="hover:text-primary hover:underline transition-colors text-left"
                          >
                            {c.name || "-"}
                          </button>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          <div className="flex flex-col">
                            {c.email && <span className="flex items-center gap-1"><Mail size={11} /> {c.email}</span>}
                            {c.phone && <span className="flex items-center gap-1"><MessageCircle size={11} /> {c.phone}</span>}
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="secondary" className="text-[10px]">{label}</Badge></TableCell>
                        <TableCell className="text-right">{c.daysSinceLast}</TableCell>
                        <TableCell className="text-right">{c.bookings}</TableCell>
                        <TableCell className="text-right">{eur(c.totalRevenue)}</TableCell>
                        <TableCell className="text-right">{eur(c.avgTicket)}</TableCell>
                      </TableRow>
                    ))}
                    {!visibleLeads.length && (
                      <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Sem clientes nesta seleção.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- RANKING ---------------- */}
        <TabsContent value="ranking" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-base flex items-center gap-2"><Trophy size={16} /> Ranking de clientes</CardTitle>
                <div className="flex gap-2">
                  <Select value={rankSort} onValueChange={(v: any) => setRankSort(v)}>
                    <SelectTrigger className="h-9 w-52"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="revenue">Maior receita total</SelectItem>
                      <SelectItem value="visits">Mais reservas</SelectItem>
                      <SelectItem value="ticket">Maior ticket médio</SelectItem>
                      <SelectItem value="recent">Mais recentes</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={rankLimit} onValueChange={setRankLimit}>
                    <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">Top 10</SelectItem>
                      <SelectItem value="25">Top 25</SelectItem>
                      <SelectItem value="50">Top 50</SelectItem>
                      <SelectItem value="all">Todos</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={exportRanking}>
                    <Download size={15} className="mr-2" /> Exportar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[600px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Contactos</TableHead>
                      <TableHead className="text-right">Reservas</TableHead>
                      <TableHead className="text-right">Encomendas</TableHead>
                      <TableHead className="text-right">Total gasto</TableHead>
                      <TableHead className="text-right">Ticket médio</TableHead>
                      <TableHead className="text-right">Última visita</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ranking.map((c, i) => (
                      <TableRow key={c.key}>
                        <TableCell>
                          {i < 3 ? (
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${
                              i === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                            }`}>{i + 1}</span>
                          ) : <span className="text-muted-foreground text-xs">{i + 1}</span>}
                        </TableCell>
                        <TableCell className="font-medium">
                          <button 
                            onClick={() => {
                              const params = new URLSearchParams();
                              params.set("search", c.name || "");
                              if (c.email) params.set("email", c.email);
                              if (c.phone) params.set("phone", c.phone);
                              navigate(`/admin/clients?${params.toString()}`);
                            }}
                            className="hover:text-primary hover:underline transition-colors text-left"
                          >
                            {c.name || "-"}
                          </button>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          <div className="flex flex-col">
                            {c.email && <span>{c.email}</span>}
                            {c.phone && <span>{c.phone}</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{c.bookings}</TableCell>
                        <TableCell className="text-right">{c.orders}</TableCell>
                        <TableCell className="text-right font-medium">{eur(c.totalRevenue)}</TableCell>
                        <TableCell className="text-right">{eur(c.avgTicket)}</TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {c.lastDate ? format(parseISO(c.lastDate), "dd MMM yyyy", { locale: pt }) : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!ranking.length && (
                      <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Ainda não há dados de clientes.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ---------------- DIALOG CAMPANHA ---------------- */}
      <Dialog open={campaignOpen} onOpenChange={setCampaignOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enviar follow-up a {selectedClients.length} cliente(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={chEmail} onCheckedChange={(v) => setChEmail(!!v)} /> Email
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={chWhats} onCheckedChange={(v) => setChWhats(!!v)} /> WhatsApp
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              Use <code className="bg-muted px-1 rounded">{"{{nome}}"}</code> ou{" "}
              <code className="bg-muted px-1 rounded">{"{{primeiro_nome}}"}</code>. As mensagens são traduzidas
              automaticamente para o idioma de cada cliente.
            </p>

            {chEmail && (
              <div className="space-y-2">
                <Label>Assunto do email</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
                <Label>Mensagem do email</Label>
                <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={9} maxLength={5000} />
              </div>
            )}

            {chWhats && (
              <div className="space-y-2">
                <Label>Mensagem de WhatsApp</Label>
                <Textarea value={whatsBody} onChange={(e) => setWhatsBody(e.target.value)} rows={8} maxLength={4000} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCampaignOpen(false)} disabled={sending}>Cancelar</Button>
            <Button onClick={sendCampaign} disabled={sending || (!chEmail && !chWhats)}>
              {sending ? "A enviar..." : <><Send size={15} className="mr-2" /> Enviar agora</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
